import { useState, useEffect, SyntheticEvent } from 'react';

import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import {
  Autocomplete,
  TextField,
  Box,
  AutocompleteInputChangeReason,
  AutocompleteRenderOptionState,
  AutocompleteRenderInputParams,
} from '@mui/material';

import { ProfileChip } from './ProfileChip';
import {
  autoCompleteSx,
  searchIconSx,
  tagContainerSx,
  underneathTagContainerSx,
  selectTextFieldStyle,
  menuItemSx,
} from './styles/autocomplete.styles';
import { ProfileFormAutoCompleteProps, OptionType } from './types';
import {
  determineIsOptionEqualToValue,
  handleFilterOptions,
  transformOptionsToValues,
  getSelectedOptions,
  createValueOptionsFromForm,
  findSelectedOption,
  mergeOptionsWithoutDuplicates,
  createOptionsFromAutocompleteValues,
} from './utils/autocomplete.utils';

type AutocompleteValue = string | OptionType | (string | OptionType)[] | null;

export const ProfileFormAutoComplete = ({
  isMultiple,
  autoCompleteOptions,
  field,
  options,
  controllerField,
  groupedFields,
  index,
  error,
  groupKey,
  chipType,
  showSearch,
  disabled,
  inlineChip,
}: ProfileFormAutoCompleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const [localOptions, setLocalOptions] = useState<OptionType[]>(autoCompleteOptions);

  const sourceOptions = field.autoCompleteEndpoint ? localOptions : options;

  // Create option objects for values in the form
  const valueOptionsFromForm = createValueOptionsFromForm(
    controllerField,
    sourceOptions,
    isMultiple,
  );

  const selectedOptions = isMultiple
    ? valueOptionsFromForm
    : findSelectedOption(controllerField, sourceOptions);

  const shouldRenderChips =
    isMultiple && Array.isArray(selectedOptions) && selectedOptions.length > 0 && chipType;

  // Update local options when form values or autocomplete options change
  useEffect(() => {
    // Add any form values not in options to the options list
    if (isMultiple && Array.isArray(controllerField.value) && controllerField.value.length > 0) {
      const newOptions = [...autoCompleteOptions];

      controllerField.value.forEach((value) => {
        // First try to find by value
        const exists = newOptions.some((option) => option.value === value);

        if (exists) return;

        // Then try to find by label
        const optionByLabel = newOptions.find((opt) => opt.label === value);

        if (optionByLabel) return;

        // If not found, add as new option
        newOptions.push({ label: value, value });
      });

      setLocalOptions(newOptions);
    } else {
      setLocalOptions(autoCompleteOptions);
    }
  }, [autoCompleteOptions, controllerField.value, isMultiple]);

  const handleInputChange = (
    _: SyntheticEvent<Element, Event>,
    newInputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === 'input' || reason === 'clear') {
      setInputValue(newInputValue);

      field.onInputChange?.(newInputValue, field.name || '');
    }
  };

  const handleOptionChange = (_: SyntheticEvent<Element, Event>, newValue: AutocompleteValue) => {
    controllerField.onChange(transformOptionsToValues(newValue));

    if (isMultiple) {
      const newOptions = createOptionsFromAutocompleteValues(newValue);

      setLocalOptions((prev) => mergeOptionsWithoutDuplicates(prev, newOptions));
    }

    setInputValue('');
  };

  const handleChipDelete = (optionToDelete: OptionType) => {
    const currentSelectedOptions = getSelectedOptions(selectedOptions || [], isMultiple);
    const updatedOptions = currentSelectedOptions.filter(
      (opt: OptionType) => opt.value !== optionToDelete.value,
    );

    controllerField.onChange(transformOptionsToValues(updatedOptions));

    setLocalOptions((prev) => [...prev]);
  };

  const handleOptionClick = (e: React.MouseEvent, selected: boolean) => {
    if (!isMultiple && selected) {
      e.preventDefault();
      e.stopPropagation();

      controllerField.onChange(null);

      setInputValue('');

      return;
    }
  };

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: OptionType,
    state: AutocompleteRenderOptionState,
  ) => {
    const { selected } = state;
    const { onClick, ...otherProps } = props;

    return (
      <Box
        component="li"
        key={option.value}
        {...otherProps}
        sx={menuItemSx}
        onClick={(e) => {
          // Call the original onClick handler for selection
          onClick?.(e);
          // Then handle our custom logic
          handleOptionClick(e, selected);
        }}
      >
        <Box width="100%">{option.label}</Box>

        {selected && (
          <AppIcon iconName="Check" diameter="20px" color="Colors/Foreground/fg-brand-primary" />
        )}
      </Box>
    );
  };

  const renderTags =
    shouldRenderChips && inlineChip
      ? (value: OptionType[]) => (
          <Box sx={tagContainerSx}>
            {value.map((option, index) => {
              const optionObj = option as OptionType;

              return (
                <ProfileChip
                  key={`${groupKey}-${field.name}-${optionObj.value}-${index}`}
                  variant={chipType || 'tag'}
                  option={optionObj}
                  onDelete={() => handleChipDelete(optionObj)}
                  groupKey={groupKey}
                  fieldName={field.name || ''}
                  index={index}
                />
              );
            })}
          </Box>
        )
      : undefined;

  const renderInput = (params: AutocompleteRenderInputParams) => {
    const hasSelectedOptions =
      isMultiple && Array.isArray(selectedOptions) && selectedOptions.length > 0;

    return (
      <TextField
        {...params}
        id={field.name}
        name={field.name}
        label={field.label}
        error={!!error?.message}
        helperText={error?.message}
        placeholder={hasSelectedOptions ? '' : field.placeholder}
        InputProps={{
          ...params.InputProps,
          endAdornment: <>{params.InputProps.endAdornment}</>,
          startAdornment: showSearch ? (
            <Box sx={searchIconSx}>
              <AppIcon diameter="24px" iconName="SearchMd" color="Gray/500" />
            </Box>
          ) : (
            params.InputProps.startAdornment
          ),
        }}
        sx={selectTextFieldStyle(
          index === Object.entries(groupedFields).length - 1,
          field.divider || false,
        )}
      />
    );
  };

  return (
    <>
      <Autocomplete<OptionType, boolean, boolean, boolean>
        disableClearable
        key={`${controllerField.value ?? ''}-${sourceOptions?.length ?? 0}`}
        multiple={isMultiple}
        options={localOptions}
        value={selectedOptions}
        inputValue={isMultiple ? inputValue : undefined}
        disabled={disabled}
        popupIcon={<AppIcon iconName="ChevronDown" diameter="20px" color="Gray/400" />}
        isOptionEqualToValue={(option, value) =>
          determineIsOptionEqualToValue(option as OptionType, value, field)
        }
        filterOptions={(options, state) =>
          handleFilterOptions(options as OptionType[], state, field)
        }
        onInputChange={handleInputChange}
        onChange={handleOptionChange}
        sx={autoCompleteSx}
        autoHighlight
        {...(isMultiple === false && field.autoCompleteEndpoint ? { freeSolo: true } : {})}
        renderOption={renderOption}
        renderTags={renderTags}
        renderInput={renderInput}
        disableCloseOnSelect={isMultiple}
      />
      {shouldRenderChips && !inlineChip && (
        <Box sx={underneathTagContainerSx}>
          {selectedOptions?.map((option, index) => {
            const optionObj = option as OptionType;

            return (
              <ProfileChip
                key={`${groupKey}-${field.name}-${optionObj.value}-${index}`}
                variant={chipType}
                option={optionObj}
                onDelete={() => handleChipDelete(optionObj)}
                groupKey={groupKey}
                fieldName={field.name || ''}
                index={index}
              />
            );
          })}
        </Box>
      )}
    </>
  );
};
